import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { productApi, extractList } from '@/lib/api';
import { Product } from '@/types';
import { debounce, formatPrice, getImageUrl } from '@/lib/utils';
export default function SearchBar() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchProducts = debounce(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await productApi.search(q);
      setResults(extractList<Product>(response.data, 'products'));
      setIsOpen(true);
    } catch {
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, 300);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    searchProducts(value);
  };

  const handleSelect = (slug: string) => {
    setIsOpen(false);
    setQuery('');
    navigate(`/products/${slug}`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setIsOpen(false);
      navigate(`/categories?search=${encodeURIComponent(query.trim())}`);
    }
  };

  return (
    <div ref={wrapperRef} className="search-bar">
      <form onSubmit={handleSubmit}>
        <input
          type="text"
          value={query}
          onChange={handleChange}
        />
        {query ? (
          <button
            type="button"
            className="search-btn"
            onClick={() => {
              setQuery('');
              setResults([]);
              setIsOpen(false);
            }}
            aria-label="Clear search"
          >
            <X size={18} />
          </button>
        ) : (
          <button type="submit" className="search-btn" aria-label="Search">
            <Search size={18} />
          </button>
        )}
      </form>

      {/* Suggestions dropdown */}
      {isOpen && (
        <div
          className="absolute top-full mt-2 w-full bg-white rounded-lg overflow-hidden z-50"
          style={{ boxShadow: 'var(--shadow-lg)', border: '1px solid var(--grey-200)' }}
        >
          {isLoading ? (
            <div className="p-4 text-center text-sm" style={{ color: 'var(--grey-500)' }}>
              Searching...
            </div>
          ) : results.length > 0 ? (
            <div className="max-h-80 overflow-y-auto">
              {results.map((product) => (
                <button
                  key={product.id}
                  onClick={() => handleSelect(product.slug)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--grey-100)] transition-colors text-left"
                >
                  <div className="relative w-10 h-10 rounded-lg overflow-hidden flex-shrink-0" style={{ background: 'var(--grey-100)' }}>
                    <img
                      src={getImageUrl(product.images[0]?.image_path || null)}
                      alt={product.name}
                      className="object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--grey-900)' }}>
                      {product.name}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--grey-500)' }}>
                      Starting from {formatPrice(product.base_customer_price)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-sm" style={{ color: 'var(--grey-500)' }}>
              No products found for &ldquo;{query}&rdquo;
            </div>
          )}
        </div>
      )}
    </div>
  );
}
